import { graphqlGLClient } from '../../api/gqlaxy/gql/queries/graphQLClient'
import {
  ClientEventName,
  ClientEventSource,
  CreateClientEventMutation,
  CreateClientEventMutationVariables
} from '../../api/gqlaxy/gql/generated/graphql'
import { CreateClientEventDocument } from '../../api/gqlaxy/gql/generated/graphql-hooks'
import * as Sentry from '@sentry/browser'
import { PATH_HELPERS } from '../../constants/navigation'

export const UTM_CONTENT = {
  VIEW_YOUR_LISTING: 'view_your_listing'
}

/**
 * Fires a client event based on the utm_content query parameter.
 * This is called during the auth callback flow to track user engagement from email links.
 *
 * @param utmContent - The utm_content query parameter value that is defined and passed in from mikasa dynamic email data templates like we do here for example: app/services/api_email_delivery/client_emails/listing_alert/dynamic_data/v1.rb (Mikasa)
 */
export const fireClientEventFromUtmContent = (utmContent: string, redirectUrl: string): void => {
  const client = graphqlGLClient()

  try {
    switch (utmContent) {
      case UTM_CONTENT.VIEW_YOUR_LISTING: {
        client.request<CreateClientEventMutation, CreateClientEventMutationVariables>(CreateClientEventDocument, {
          clientEventInput: {
            eventName: ClientEventName.ViewedTheirOwnListing,
            eventSource: ClientEventSource.ListingDetailed,
            listingId: redirectUrl.split('/').pop(), // grabs the listing id from the redirect url which is formatted like this when utm_content is 'view_your_listing': /home-search/{customerProfileId/listing/{listingId}
            properties: {
              activity_source: 'email'
            }
          }
        })
        break
      }
      default:
        // No-op we don't handle other utm_content values yet
        break
    }
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'clientEvent',
      message: 'Error firing client event from utm_content',
      level: 'error',
      data: {
        utmContent
      }
    })
    Sentry.captureException(error)
  }
}

/**
 * Fires listing view events when redirecting to a listing details page.
 * This is called during the auth callback flow to track listing views.
 * Mirrors the events fired by the navigateToListing function in ListingCardNew.tsx:
 * - View event with ListingDetailed source
 * - DetailsPageViewedEvent
 *
 * @param redirectUrl - The redirect URL that may contain a listing details page path
 */
export const fireListingViewEventsFromRedirectUrl = (redirectUrl: string): void => {
  const client = graphqlGLClient()

  try {
    const url = new URL(redirectUrl)
    const pathname = url.pathname

    // Check if the path matches the listing details pattern: /home-search/:customerProfileId/listing/:listingId
    const listingDetailsMatch = pathname.match(PATH_HELPERS.listing.regex)

    if (listingDetailsMatch) {
      const listingId = listingDetailsMatch[2]

      Promise.all([
        client.request<CreateClientEventMutation, CreateClientEventMutationVariables>(CreateClientEventDocument, {
          clientEventInput: {
            eventName: ClientEventName.View,
            eventSource: ClientEventSource.ListingDetailed,
            listingId: listingId
          }
        }),
        client.request<CreateClientEventMutation, CreateClientEventMutationVariables>(CreateClientEventDocument, {
          clientEventInput: {
            eventName: ClientEventName.DetailsPageViewedEvent,
            listingId: listingId
          }
        })
      ]).catch(error => {
        Sentry.addBreadcrumb({
          category: 'clientEvent',
          message: 'Error firing listing view events from redirect URL',
          level: 'error',
          data: {
            redirectUrl
          }
        })
        Sentry.captureException(error)
      })
    }
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'clientEvent',
      message: 'Error firing listing view events from redirect URL',
      level: 'error',
      data: {
        redirectUrl
      }
    })
    Sentry.captureException(error)
  }
}
